/**
 * Camera reset hook for tab switching
 *
 * This hook subscribes to scene tab changes and smoothly transitions
 * the camera position when switching between tabs. Each tab maintains
 * its own camera position state.
 *
 * Features:
 * - Subscribes to sceneStore activeTabId changes
 * - Smoothly animates camera to new tab's position using lerp
 * - Resets OrbitControls target to origin
 * - Clears selected tool on tab switch
 * - Skips reset on initial mount
 */

import { useEffect, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Vector3 } from 'three';
import { useSceneStore } from '../../../stores/sceneStore';
import { useUiStore } from '../../../stores/uiStore';
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib';

interface CameraResetOptions {
  smooth?: boolean;
  lerpFactor?: number;
}

/**
 * Hook to reset camera position when the active tab changes
 *
 * Subscribes to sceneStore and smoothly transitions camera to the
 * new tab's camera position when switching tabs. Also clears the
 * selected tool to avoid confusion across tabs.
 *
 * @param controlsRef - Ref to OrbitControls instance
 * @param options - Configuration options
 * @param options.smooth - Enable smooth lerp transitions (default: true)
 * @param options.lerpFactor - Speed of camera transition, 0-1 (default: 0.1)
 */
export function useCameraReset(
  controlsRef: React.RefObject<OrbitControlsImpl | null>,
  options: CameraResetOptions = {}
) {
  const { smooth = true, lerpFactor = 0.1 } = options;
  const { camera } = useThree();
  const previousTabId = useRef<string | null>(null);
  const targetPosition = useRef<Vector3 | null>(null);
  const isAnimating = useRef<boolean>(false);

  useEffect(() => {
    // Subscribe to active tab changes
    const unsubscribe = useSceneStore.subscribe((state) => {
      const activeTabId = state.activeTabId;

      // Skip on initial mount
      if (previousTabId.current === null) {
        previousTabId.current = activeTabId;
        return;
      }

      // Skip if tab hasn't actually changed
      if (previousTabId.current === activeTabId) {
        return;
      }

      previousTabId.current = activeTabId;

      // Clear selected tool on tab switch
      useUiStore.getState().deselectToolBlock();

      // Get the new tab's camera position
      const tab = state.tabs.find((t) => t.id === activeTabId);

      if (!tab) return;

      const { cameraPosition } = tab;

      if (smooth) {
        // Set target for smooth lerp animation
        targetPosition.current = new Vector3(
          cameraPosition.x,
          cameraPosition.y,
          cameraPosition.z
        );
        isAnimating.current = true;
      } else {
        // Immediate reset without animation
        camera.position.set(
          cameraPosition.x,
          cameraPosition.y,
          cameraPosition.z
        );

        // Reset OrbitControls target to origin
        if (controlsRef.current) {
          controlsRef.current.target.set(0, 0, 0);
          controlsRef.current.update();
        }
      }
    });

    return unsubscribe;
  }, [camera, controlsRef, smooth, lerpFactor]);

  // Smooth lerp animation using useFrame
  useFrame(() => {
    if (!targetPosition.current || !smooth || !isAnimating.current) return;

    const threshold = 0.01; // Stop lerping when close enough to prevent jitter

    // Lerp camera position towards target
    camera.position.lerp(targetPosition.current, lerpFactor);

    // Calculate distance to target
    const distance = camera.position.distanceTo(targetPosition.current);

    // Snap to final position when close enough
    if (distance < threshold) {
      camera.position.copy(targetPosition.current);
      targetPosition.current = null;
      isAnimating.current = false;
    }

    // Update OrbitControls
    if (controlsRef.current) {
      controlsRef.current.target.set(0, 0, 0);
      controlsRef.current.update();
    }
  });
}
